import {
    getAuth,
    signInWithEmailAndPassword,
    Auth,
    UserCredential,
    AuthError,
    browserSessionPersistence,
    setPersistence
  } from "firebase/auth";
  import { app } from "@config/firebase"; // Import your existing Firebase app
  import { isInternalPortalMode } from "./portalHelper";
  
  // User information interface
  interface UserInfo {
    uid: string;
    email: string | null;
    emailVerified: boolean;
    displayName: string | null;
    photoURL: string | null;
    creationTime?: string;
    lastSignInTime?: string;
  }
  
  // Response interface
  interface AuthResponse {
    success: boolean;
    user: UserInfo | null;
    error: string | null;
  }
  
  /**
   * Firebase authentication function for email/password sign in
   * @param email - User's email address
   * @param password - User's password
   * @param redirectToAdmin - Whether to redirect to admin dashboard after successful login
   * @returns Promise with authentication result
   */
  const firebaseEmailSignIn = async (
    email: string, 
    password: string,
    redirectToAdmin: boolean = true
  ): Promise<AuthResponse> => {
    // Response object
    const response: AuthResponse = {
      success: false,
      user: null,
      error: null
    };
  
    try {
      // Validate inputs
      if (!email || !password) {
        throw new Error("Email and password are required");
      }
  
      // Use existing Firebase app
      const auth: Auth = getAuth(app);
      
      // Set session persistence to browser session (lasts until browser is closed)
      // This is the closest built-in option to a 1-hour timeout
      await setPersistence(auth, browserSessionPersistence);
  
      // Attempt to sign in
      const userCredential: UserCredential = await signInWithEmailAndPassword(auth, email, password);

      // Get the ID token with a forced refresh (ensures token is current)
      await userCredential.user.getIdToken(true);
      
      // Set a timeout to force logout after 1 hour (3600000 ms)
      setTimeout(() => {
        auth.signOut().then(() => {
          console.log("Session expired after 1 hour");
          // Redirect to login page after logout
          window.location.href = "/";
        });
      }, 3600000);
      
      // Return success response
      response.success = true;
      response.user = {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        emailVerified: userCredential.user.emailVerified,
        displayName: userCredential.user.displayName,
        photoURL: userCredential.user.photoURL,
        creationTime: userCredential.user.metadata?.creationTime,
        lastSignInTime: userCredential.user.metadata?.lastSignInTime
      };
      
      // Redirect to admin dashboard if requested
      if (redirectToAdmin && response.success) {
        const isInternal = isInternalPortalMode();

        if (isInternal) {
          window.location.href = "/?internal=true";
        } else {
          window.location.href = "/admin/intros/dashboard";
        }
      }
  
      return response;
    } catch (error) {
      // Handle specific Firebase auth errors
      response.success = false;
      
      // Cast error to AuthError to access the code property
      const authError = error as AuthError;
      
      // Map common Firebase error codes to user-friendly messages
      switch (authError.code) {
        case "auth/invalid-email":
          response.error = "Invalid email address format";
          break;
        case "auth/user-disabled":
          response.error = "This user account has been disabled";
          break;
        case "auth/user-not-found":
          response.error = "No account found with this email";
          break;
        case "auth/wrong-password":
          response.error = "Incorrect password";
          break;
        case "auth/too-many-requests":
          response.error = "Too many failed login attempts. Please try again later";
          break;
        case "auth/network-request-failed":
          response.error = "Network error. Please check your connection";
          break;
        default:
          response.error = authError.message || "Authentication failed. Please try again";
      }
      
      return response;
    }
  };
  
  export default firebaseEmailSignIn;